import {
  address,
  nonDivisibleSequentialInstructionPlan,
  type Address,
  type Instruction,
  type TransactionSigner,
} from '@solana/kit';
import {
  TOKEN_PROGRAM_ADDRESS,
  findAssociatedTokenPda,
  getCreateAssociatedTokenIdempotentInstructionAsync,
  getTransferCheckedInstruction,
} from '@solana-program/token';
import { formatTokenAmount, parseTokenAmount } from './amount';
import type { BlackBtcClient } from './wallet';

const commitment = 'confirmed';
// Token-2022 is accepted solely for the public Black Bitcoin mint selected by
// the operator. Keeping this allow-list prevents an arbitrary extension-enabled
// mint from inheriting the simple transfer flow below.
export const BLACK_BITCOIN_TOKEN_2022_MINT = address('GgazaEccWY7t5BegwmJyUbWPCNQNv1HWmdag6Vp2pump');
export const TOKEN_2022_PROGRAM_ADDRESS = address('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb');
const ALLOWED_TOKEN_2022_EXTENSIONS = new Set(['metadataPointer', 'tokenMetadata']);

export type TokenSnapshot = {
  mint: string;
  decimals: number;
  balanceBaseUnits: bigint;
  balanceDisplay: string;
  solLamports: bigint;
};

export type PreparedBlackBtcPayment = {
  instructions: readonly Instruction[];
  recipient: string;
  amount: string;
  baseUnits: bigint;
  decimals: number;
};

type SourceTokenAccount = {
  address: Address;
  amount: bigint;
};

type RpcEnvelope<T> = {
  result?: T;
  error?: { message?: string };
};

type ParsedAccountInfo = {
  owner?: string;
  data?: {
    parsed?: {
      type?: string;
      info?: {
        decimals?: number;
        mint?: string;
        tokenAmount?: { amount?: string };
        extensions?: Array<{ extension?: string }>;
      };
    };
  };
};

type MintDetails = {
  decimals: number;
  tokenProgram: Address;
};

async function rpcCall<T>(rpcUrl: string, method: string, params: unknown[]): Promise<T> {
  const response = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
  });
  if (!response.ok) throw new Error(`Solana RPC returned HTTP ${response.status}.`);
  const body = await response.json() as RpcEnvelope<T>;
  if (body.error) throw new Error(body.error.message ?? 'Solana RPC rejected the request.');
  if (body.result === undefined) throw new Error('Solana RPC returned an incomplete response.');
  return body.result;
}

export function parseSolanaAddress(value: string, label = 'address'): Address {
  try {
    return address(value.trim());
  } catch {
    throw new Error(`${label} must be a valid Solana address.`);
  }
}

export function resolveSupportedTokenProgram(
  mint: Address,
  owner: string | undefined,
  extensions: readonly string[] = [],
): Address {
  if (owner === TOKEN_PROGRAM_ADDRESS) return TOKEN_PROGRAM_ADDRESS;
  if (owner !== TOKEN_2022_PROGRAM_ADDRESS || mint !== BLACK_BITCOIN_TOKEN_2022_MINT) {
    throw new Error('The configured Black BTC mint is not a supported SPL Token or approved Token-2022 mint.');
  }
  if (extensions.some((extension) => !ALLOWED_TOKEN_2022_EXTENSIONS.has(extension))) {
    throw new Error('The configured Token-2022 mint has unsupported transfer-affecting extensions.');
  }
  return TOKEN_2022_PROGRAM_ADDRESS;
}

async function readMintDetails(rpcUrl: string, mint: Address): Promise<MintDetails> {
  const result = await rpcCall<{ value: ParsedAccountInfo | null }>(rpcUrl, 'getAccountInfo', [
    mint,
    { commitment, encoding: 'jsonParsed' },
  ]);
  if (!result.value) throw new Error('The configured Black BTC mint does not exist on this Solana cluster.');
  if (result.value.data?.parsed?.type !== 'mint') {
    throw new Error('The configured Black BTC address is not an SPL token mint.');
  }
  const extensions = result.value.data.parsed.info?.extensions
    ?.map(({ extension }) => extension)
    .filter((extension): extension is string => Boolean(extension)) ?? [];
  const tokenProgram = resolveSupportedTokenProgram(mint, result.value.owner, extensions);
  const decimals = result.value.data.parsed.info?.decimals;
  if (decimals === undefined || !Number.isInteger(decimals) || decimals < 0 || decimals > 18) {
    throw new Error('The Black BTC mint returned invalid decimal metadata.');
  }
  return { decimals, tokenProgram };
}

async function sourceAccounts(
  rpcUrl: string,
  owner: Address,
  mint: Address,
  tokenProgram: Address,
): Promise<SourceTokenAccount[]> {
  const result = await rpcCall<{ value: Array<{ pubkey?: string; account?: ParsedAccountInfo }> }>(
    rpcUrl,
    'getTokenAccountsByOwner',
    [owner, { mint }, { commitment, encoding: 'jsonParsed' }],
  );
  return result.value.flatMap(({ pubkey, account }) => {
    const info = account?.data?.parsed?.info;
    const rawAmount = info?.tokenAmount?.amount;
    if (
      !pubkey
      || account?.owner !== tokenProgram
      || info?.mint !== mint
      || !rawAmount
      || !/^\d+$/.test(rawAmount)
    ) return [];
    const amount = BigInt(rawAmount);
    return amount > 0n ? [{ address: parseSolanaAddress(pubkey, 'Token account'), amount }] : [];
  }).sort((left, right) => left.amount === right.amount ? 0 : left.amount > right.amount ? -1 : 1);
}

async function readSolBalance(rpcUrl: string, owner: Address): Promise<bigint> {
  const result = await rpcCall<{ value: number }>(rpcUrl, 'getBalance', [owner, { commitment }]);
  if (!Number.isSafeInteger(result.value) || result.value < 0) {
    throw new Error('Solana RPC returned an invalid SOL balance.');
  }
  return BigInt(result.value);
}

export async function loadTokenSnapshot(
  rpcUrl: string,
  ownerAddress: string,
  mintAddress: string,
): Promise<TokenSnapshot> {
  const owner = parseSolanaAddress(ownerAddress, 'Wallet');
  const mint = parseSolanaAddress(mintAddress, 'Black BTC mint');
  const [mintDetails, solLamports] = await Promise.all([
    readMintDetails(rpcUrl, mint),
    readSolBalance(rpcUrl, owner),
  ]);
  const accounts = await sourceAccounts(rpcUrl, owner, mint, mintDetails.tokenProgram);
  const balanceBaseUnits = accounts.reduce((total, account) => total + account.amount, 0n);
  return {
    mint,
    decimals: mintDetails.decimals,
    balanceBaseUnits,
    balanceDisplay: formatTokenAmount(balanceBaseUnits, mintDetails.decimals),
    solLamports,
  };
}

export async function prepareBlackBtcPayment(input: {
  rpcUrl: string;
  signer: TransactionSigner;
  recipient: string;
  mintAddress: string;
  amount: string;
}): Promise<PreparedBlackBtcPayment> {
  const sender = input.signer.address;
  const recipient = parseSolanaAddress(input.recipient, 'Recipient');
  if (recipient === sender) throw new Error('Recipient must be different from the connected wallet.');
  const mint = parseSolanaAddress(input.mintAddress, 'Black BTC mint');
  const mintDetails = await readMintDetails(input.rpcUrl, mint);
  const { decimals, tokenProgram } = mintDetails;
  const accounts = await sourceAccounts(input.rpcUrl, sender, mint, tokenProgram);
  const baseUnits = parseTokenAmount(input.amount, decimals);
  const totalBalance = accounts.reduce((total, account) => total + account.amount, 0n);
  if (totalBalance < baseUnits) throw new Error('Insufficient Black BTC balance.');

  const [recipientTokenAccount] = await findAssociatedTokenPda({
    mint,
    owner: recipient,
    tokenProgram,
  });
  const instructions: Instruction[] = [
    await getCreateAssociatedTokenIdempotentInstructionAsync({
      payer: input.signer,
      mint,
      owner: recipient,
      tokenProgram,
    }),
  ];

  let remaining = baseUnits;
  for (const account of accounts) {
    if (remaining === 0n) break;
    const transferAmount = account.amount < remaining ? account.amount : remaining;
    instructions.push(getTransferCheckedInstruction({
      source: account.address,
      mint,
      destination: recipientTokenAccount,
      authority: input.signer,
      amount: transferAmount,
      decimals,
    }, { programAddress: tokenProgram }));
    remaining -= transferAmount;
  }
  if (remaining !== 0n) throw new Error('Unable to select sufficient token accounts for this payment.');

  return {
    instructions,
    recipient,
    amount: input.amount.trim(),
    baseUnits,
    decimals,
  };
}

export async function signAndSendBlackBtcPayment(input: {
  client: BlackBtcClient;
  payment: PreparedBlackBtcPayment;
}): Promise<string> {
  const plan = nonDivisibleSequentialInstructionPlan([...input.payment.instructions]);
  const result = await input.client.sendTransaction(plan);
  return result.context.signature;
}
